import { getVulnerabilityMap, getVulnerabilityLabel } from '../../utils/vulnerabilityMapper';

interface VulnerabilityIndicatorProps {
  vulnerability: string;
  vulnerabilityText?: string; // Raw text from JSON (e.g., "Favorable", "Unfavorable")
  decisionSeat?: 'N' | 'E' | 'S' | 'W';
  dealer?: 'N' | 'E' | 'S' | 'W';
  compact?: boolean; // Smaller size for embedding inside play tables
}

export function VulnerabilityIndicator({
  vulnerability,
  vulnerabilityText,
  decisionSeat = 'S',
  dealer,
  compact = false,
}: VulnerabilityIndicatorProps) {
  const vulnMap = getVulnerabilityMap(vulnerability, decisionSeat);

  // Derive the label from the SAME raw `vulnerability` value the board renders
  // from, so the text can never disagree with the coloured indicator. The
  // separate `vulnerabilityText` prop (sourced independently from the JSON) is
  // only honoured as an override when it is already a recognised single-word
  // label (Neither/Both/Favorable/Unfavorable). Pair-form values such as
  // "North-South"/"East-West" — which some data carries — are NOT seat-relative
  // and must NOT be shown verbatim, so we ignore them and derive the
  // Favorable/Unfavorable label from Jeff's seat instead. "Unknown
  // Vulnerability" shows only when the board value itself is unknown.
  const SINGLE_WORD_LABELS = new Set(['neither', 'both', 'favorable', 'unfavorable']);
  const labelOverride =
    vulnerabilityText && SINGLE_WORD_LABELS.has(vulnerabilityText.trim().toLowerCase())
      ? vulnerabilityText
      : null;
  const displayText = labelOverride ?? getVulnerabilityLabel(vulnerability, decisionSeat);

  // Border colors for vulnerable (red) and non-vulnerable (light gray)
  const vulnColor = '#dc2626'; // red-600
  const nonVulnColor = '#d8ceae'; // themed neutral border

  const getBorderColor = (seat: 'N' | 'E' | 'S' | 'W') => {
    return vulnMap[seat] ? vulnColor : nonVulnColor;
  };

  // Text color for dealer "D": white on red, dark on gray
  const getDTextColor = (seat: 'N' | 'E' | 'S' | 'W') => {
    return vulnMap[seat] ? '#ffffff' : '#22221d';
  };

  return (
    <div className="flex flex-col items-center flex-shrink-0">
      {/* Vulnerability text label above the indicator (FR-009) — the label
          is the top of the Vulnerability indicator control and must align
          with the top of the Auction container in the same row. */}
      <div className={`text-ink font-semibold ${compact ? 'text-xs mb-0.5' : 'text-base short:text-xs mb-1 short:mb-0.5'} text-center`}>
        {displayText || <>Unknown<br />Vulnerability</>}
      </div>

      {/* Vulnerability indicator box with rectangular flap borders (FR-009) */}
      {/* CSS handles responsive sizing via vuln-indicator class and short: variant */}
      <div className={`${compact ? 'vuln-indicator-compact' : 'vuln-indicator'} relative`}>
        {/* North flap */}
        <div
          className="vuln-flap-h absolute top-0 left-[var(--vuln-flap)]"
          style={{ backgroundColor: getBorderColor('N') }}
        >
          {dealer === 'N' && (
            <span
              className={`absolute font-bold ${compact ? 'text-[0.45rem]' : 'text-sm'} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
              style={{ color: getDTextColor('N') }}
            >
              D
            </span>
          )}
        </div>

        {/* South flap */}
        <div
          className="vuln-flap-h absolute bottom-0 left-[var(--vuln-flap)]"
          style={{ backgroundColor: getBorderColor('S') }}
        >
          {dealer === 'S' && (
            <span
              className={`absolute font-bold ${compact ? 'text-[0.45rem]' : 'text-sm'} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
              style={{ color: getDTextColor('S') }}
            >
              D
            </span>
          )}
        </div>

        {/* East flap */}
        <div
          className="vuln-flap-v absolute top-[var(--vuln-flap)] right-0"
          style={{ backgroundColor: getBorderColor('E') }}
        >
          {dealer === 'E' && (
            <span
              className={`absolute font-bold ${compact ? 'text-[0.45rem]' : 'text-sm'} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
              style={{ color: getDTextColor('E') }}
            >
              D
            </span>
          )}
        </div>

        {/* West flap */}
        <div
          className="vuln-flap-v absolute top-[var(--vuln-flap)] left-0"
          style={{ backgroundColor: getBorderColor('W') }}
        >
          {dealer === 'W' && (
            <span
              className={`absolute font-bold ${compact ? 'text-[0.45rem]' : 'text-sm'} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
              style={{ color: getDTextColor('W') }}
            >
              D
            </span>
          )}
        </div>

        {/* Inner cyan background */}
        <div className="vuln-inner absolute bg-cyan-100" />
      </div>

      {/* CSS for vulnerability indicator sizing - responsive via clamp() (FR-003) */}
      <style>{`
        /* Dynamic sizes using clamp() for viewport-adaptive scaling */
        .vuln-indicator {
          --vuln-total-w: clamp(5rem, 12dvh, 8.75rem);
          --vuln-total-h: clamp(5rem, 12dvh, 8.75rem);
          --vuln-inner-w: clamp(3.5rem, 8.5dvh, 6.25rem);
          --vuln-inner-h: clamp(3.5rem, 8.5dvh, 6.25rem);
          --vuln-flap: clamp(0.75rem, 1.7dvh, 1.25rem);
          width: var(--vuln-total-w);
          height: var(--vuln-total-h);
        }

        /* Horizontal flaps (North/South) */
        .vuln-flap-h {
          width: var(--vuln-inner-w);
          height: var(--vuln-flap);
        }

        /* Vertical flaps (East/West) */
        .vuln-flap-v {
          width: var(--vuln-flap);
          height: var(--vuln-inner-h);
        }

        /* Inner cyan area */
        .vuln-inner {
          top: var(--vuln-flap);
          left: var(--vuln-flap);
          width: var(--vuln-inner-w);
          height: var(--vuln-inner-h);
        }

        /* Compact variant for embedding in play tables */
        .vuln-indicator-compact {
          --vuln-total-w: 3.5rem;
          --vuln-total-h: 3.5rem;
          --vuln-inner-w: 2.4rem;
          --vuln-inner-h: 2.4rem;
          --vuln-flap: 0.55rem;
          width: var(--vuln-total-w);
          height: var(--vuln-total-h);
        }
        .vuln-indicator-compact .vuln-flap-h {
          width: var(--vuln-inner-w);
          height: var(--vuln-flap);
        }
        .vuln-indicator-compact .vuln-flap-v {
          width: var(--vuln-flap);
          height: var(--vuln-inner-h);
        }
        .vuln-indicator-compact .vuln-inner {
          top: var(--vuln-flap);
          left: var(--vuln-flap);
          width: var(--vuln-inner-w);
          height: var(--vuln-inner-h);
        }
      `}</style>
    </div>
  );
}
